import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  Signal,
  ViewChild
} from '@angular/core';
import { MessageItemComponent } from '../message-item/message-item.component';
import { MessagePosition } from '../../../application/enums';
import { MessageGroup, MessageReaction, MessageViewModel } from '../../../application/interfaces';
import { MessageListViewService } from './view-service/message-list.view-service';

@Component({
  selector: 'lib-custom-message-list',
  templateUrl: './custom-message-list.component.html',
  styleUrls: ['./custom-message-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MessageItemComponent],
  providers: [MessageListViewService]
})
export class CustomMessageListComponent implements AfterViewChecked {
  @ViewChild('scrollAnchor') private scrollAnchor!: ElementRef;

  private readonly viewService: MessageListViewService = inject(MessageListViewService);

  private shouldScrollToBottom: boolean = true;
  private previousMessageCount: number = 0;

  readonly messageGroups: Signal<MessageGroup[]> = this.viewService.messageGroups;
  readonly firstUnreadMessageId: Signal<string | null> = this.viewService.firstUnreadMessageId;

  constructor() {
    this.watchForNewMessages();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
    }
  }

  trackByGroupDate(_index: number, group: MessageGroup): string {
    return group.date;
  }

  trackByMessageId(_index: number, message: MessageViewModel): string {
    const reactionKey: string = message.reactions.map((r: MessageReaction) => `${r.type}:${r.count}`).join(',');
    return `${message.id}-${reactionKey}`;
  }

  getMessagePosition(messages: MessageViewModel[], index: number): MessagePosition {
    const currentMessage: MessageViewModel = messages[index];
    const previousMessage: MessageViewModel | null = index > 0 ? messages[index - 1] : null;
    const nextMessage: MessageViewModel | null = index < messages.length - 1 ? messages[index + 1] : null;

    const hasSamePrevUser: boolean = this.isSameGroup(previousMessage, currentMessage);
    const hasSameNextUser: boolean = this.isSameGroup(currentMessage, nextMessage);

    if (!hasSamePrevUser && !hasSameNextUser) {
      return MessagePosition.SINGLE;
    }
    if (!hasSamePrevUser && hasSameNextUser) {
      return MessagePosition.FIRST;
    }
    if (hasSamePrevUser && hasSameNextUser) {
      return MessagePosition.MIDDLE;
    }
    return MessagePosition.LAST;
  }

  private isSameGroup(firstMessage: MessageViewModel | null, secondMessage: MessageViewModel | null): boolean {
    if (!firstMessage || !secondMessage) {
      return false;
    }

    if (firstMessage.userId !== secondMessage.userId) {
      return false;
    }

    const fiveMinutesMs: number = 5 * 60 * 1000;
    const timeDiff: number = secondMessage.createdAt.getTime() - firstMessage.createdAt.getTime();

    return timeDiff < fiveMinutesMs;
  }

  private watchForNewMessages(): void {
    effect(() => {
      const currentCount: number = this.viewService.messageCount();

      if (currentCount > this.previousMessageCount) {
        setTimeout(() => this.scrollToBottom(), 150);
      }
      this.previousMessageCount = currentCount;
    });
  }

  private scrollToBottom(): void {
    if (this.scrollAnchor?.nativeElement) {
      this.scrollAnchor.nativeElement.scrollIntoView({ behavior: 'instant', block: 'end' });
      this.shouldScrollToBottom = false;
    }
  }
}
